import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, App, Button, Checkbox, Modal, Radio, Typography } from 'antd';

import DialogLoadState from '../common/DialogLoadState';
import EmptyState from '../common/EmptyState';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { Skeleton } from '../loading';
import { DUPLICATE } from '../icons';
import { normalizeId } from '../../utils/ids';
import { MISSING_REASON, copyItem, copyRefusal, getCopyTargets } from '../../services/itemCopies';

const { Text } = Typography;

const targetKey = target => `${normalizeId(target?.collection?._id)}/${normalizeId(target?.card?._id)}`;

/** "Servings (a different kind of field here)", a column as "Ingredient › Amount". */
export function missingText(entry) {
	const name = entry.group ? `${entry.group} › ${entry.label || entry.name}` : (entry.label || entry.name);
	const why = MISSING_REASON[entry.reason];
	return why ? `${name} (${why})` : name;
}

/** "1 file is copied", "2 files aren't copied: …". */
export function filesText(files) {
	const copied = Number(files?.copied) || 0;
	const notCopied = Number(files?.notCopied) || 0;
	const parts = [];
	if (copied) parts.push(`${copied} ${copied === 1 ? 'file is' : 'files are'} copied`);
	if (notCopied) parts.push(`${notCopied} ${notCopied === 1 ? 'file isn\'t' : 'files aren\'t'} copied: you can't download ${notCopied === 1 ? 'it' : 'them'}, or that collection doesn't take attachments`);
	return parts.join('; ');
}

/**
 * COPY TO… (KPR-1024; keepr-api docs/SCHEMA.md § Copies). Copies the record
 * into one of the person's own collections that has the same card — one they
 * can add records to — and names, before anything is written, what that
 * collection's card leaves out. "Show my copy instead of the original" (on by
 * default ⚑) makes the copy STAND IN for its original in a combined list.
 *
 * The targets come from the API (GET copy-targets): it alone decides where a
 * record may go. On success the copy opens in its collection; a value the
 * target refused is named in a warning.
 */
export default function CopyItemModal({ open, onClose, item, card, cardType, title }) {
	const navigate = useNavigate();
	const { message } = App.useApp();
	const itemId = normalizeId(item?._id);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [attempt, setAttempt] = useState(0);
	const [chosen, setChosen] = useState(null);
	const [standIn, setStandIn] = useState(true);
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState(null);

	useEffect(() => {
		if (!open) return undefined;
		setData(null);
		setError(null);
		setRefusal(null);
		setStandIn(true);
		setChosen(null);
		if (!itemId) return undefined;
		let active = true;
		setLoading(true);
		getCopyTargets(itemId)
			.then((answer) => {
				if (!active) return;
				setData(answer);
				const first = Array.isArray(answer?.targets) ? answer.targets[0] : null;
				setChosen(first ? targetKey(first) : null);
			})
			.catch((e) => { if (active) setError(e); })
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [open, itemId, attempt]);

	const targets = useMemo(() => (Array.isArray(data?.targets) ? data.targets : []), [data]);
	const target = targets.find(t => targetKey(t) === chosen) || null;
	// A card's name beside its collection only where the collection alone
	// would not say which (two targets in one collection, or cards that differ).
	const showCard = useMemo(() => {
		const names = new Set(targets.map(t => t.card?.name || ''));
		const collections = new Set(targets.map(t => normalizeId(t.collection?._id)));
		return names.size > 1 || collections.size < targets.length;
	}, [targets]);

	const submit = async () => {
		if (!target || busy) return;
		setBusy(true);
		setRefusal(null);
		try {
			const answer = await copyItem(itemId, { collection: normalizeId(target.collection._id), card: normalizeId(target.card._id), standIn });
			const where = target.collection?.name || 'your collection';
			message.success(`Copied to ${where}.`);
			if (Array.isArray(answer?.dropped) && answer.dropped.length) {
				message.warning(`${where} didn't take ${answer.dropped.map(d => d.label || d.name).join(', ')}; the rest was copied.`);
			}
			onClose?.();
			const copyId = normalizeId(answer?.item?._id);
			if (copyId) navigate(`/collections/${normalizeId(target.collection._id)}/items/${copyId}`);
		} catch (e) {
			setRefusal(copyRefusal(e));
		} finally {
			setBusy(false);
		}
	};

	const footer = (
		<>
			<Button onClick={onClose}>Cancel</Button>
			<Button type="primary" onClick={submit} loading={busy} disabled={!target}>
				Copy
			</Button>
		</>
	);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="Copy to…"
			footer={targets.length ? footer : null}
			destroyOnHidden
			className="copy-item"
		>
			<div className="modal-identity">
				<RecordIdentity
					kicker={cardType}
					badge={<RecordBadge kind="item" record={card} title={title} />}
				/>
			</div>
			<DialogLoadState
				loaded={data !== null}
				loading={loading}
				error={Boolean(error)}
				errorDetail={error ? copyRefusal(error, 'Where this record can go could not be loaded.') : undefined}
				isEmpty={data !== null && !targets.length}
				empty={(
					<EmptyState
						icon={<DUPLICATE />}
						title="No collection of yours can take it"
						description="Copy to… needs a collection of yours with the same card that you can add records to."
						learnMore="items/copy-a-record-into-your-collection"
					/>
				)}
				label="where this record can be copied"
				onRetry={() => setAttempt(n => n + 1)}
				skeleton={<Skeleton variant="list-row" count={3} />}
				minHeight={160}
			>
				{targets.length ? (
					<div className="copy-item__body">
						<Text className="copy-item__label" id="copy-item-targets">Copy into</Text>
						<Radio.Group
							className="copy-item__targets"
							value={chosen}
							onChange={event => setChosen(event.target.value)}
							aria-labelledby="copy-item-targets"
						>
							{targets.map(t => (
								<Radio key={targetKey(t)} value={targetKey(t)} className="copy-item__target" data-testid="copy-item-target">
									<RecordBadge kind="collection" record={t.collection} title={t.collection?.name || 'A collection'} size="xs" />
									{showCard && t.card?.name ? <span className="copy-item__card">{t.card.name}</span> : null}
								</Radio>
							))}
						</Radio.Group>
						{data?.more ? <Text type="secondary" className="copy-item__more">Your first {targets.length} collections are listed.</Text> : null}
						{target && target.missing?.length ? (
							<Text type="secondary" className="copy-item__missing" data-testid="copy-item-missing">
								Not copied: {target.missing.map(missingText).join('; ')}.
							</Text>
						) : null}
						{target && filesText(target.files) ? (
							<Text type="secondary" className="copy-item__files">{filesText(target.files)}.</Text>
						) : null}
						<Checkbox className="copy-item__stand-in" checked={standIn} onChange={event => setStandIn(event.target.checked)}>
							Show my copy instead of the original
						</Checkbox>
						<Text type="secondary" className="copy-item__stand-in-help">
							In a combined list that has both, you see your copy and not the original. You can change it later on the copy.
						</Text>
						{refusal ? <Alert type="error" showIcon message={refusal} className="copy-item__refusal" /> : null}
					</div>
				) : null}
			</DialogLoadState>
		</Modal>
	);
}
